import React, { useEffect, useState, useRef, useCallback } from "react";
import "./questions.scss";
import Loading from "../../../../layout/loader/Loading";
import { useDispatch, useSelector } from "react-redux";
import { updateNewAssessmentVariable } from "../../../../../redux/slices/globalVariableSlice";
import { useLocation, useParams } from "react-router-dom";
import axiosInstance from "../../../../../httpInterceptor";
import { toast } from "react-toastify";
import RingLoader from "react-spinners/RingLoader";
import ScaleLoader from "react-spinners/ScaleLoader";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";

import { IoIosArrowDown, IoIosArrowForward } from "react-icons/io";

type Response = string | string[];

interface QuestionResponse {
  category: string;
  questionid: string;
  response: Response;
  remarks: string;
  isRequire: boolean;
  score?: string;
}

// Normalized question + answer shape after merging qna details with question metadata
interface QnaDetail {
  questionid: string;
  category: string;
  set_name: string;
  description: string;
  type: string;
  values?: { option: string; response: string; score?: number }[]; // Include question values for dropdown scores
  response: Response;
  remarks: string;
  isRequire: boolean;
  score: string;
}

const Question = ({ onData, projectid, setname, onScoreUpdate, projectstatus, reasonforchange, fromProjectReadiness }: any) => {
  const { set_name } = useParams<{ set_name: string }>();
  const location = useLocation();
  const dispatch = useDispatch();
  const [questions, setQuestions] = useState<any[]>([]);
  const [responses, setResponses] = useState<QuestionResponse[]>([]);
  const [assessmentData, setAssessmentData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState<{ [key: string]: Boolean }>({});
  const [progess, setProgess] = useState(false);
  // Track missing remarks and responses for required questions
  const [missingRemarks, setMissingRemarks] = useState<string[]>([]);
  const [missingResponses, setMissingResponses] = useState<string[]>([]);
  const [scorewiseData, setScorewiseData] = useState<{ [key: string]: number }>({});
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [hasSavedDraft, setHasSavedDraft] = useState(false);
  // State to track question order from CreateQuestion page
  const [questionOrder, setQuestionOrder] = useState<{ [key: string]: string[] }>({});
  
  // Ref that always mirrors the questions state – lets effects sync immediately
  // without adding questions to their dependency arrays
  const questionsRef = useRef<any[]>([]);
  useEffect(() => { questionsRef.current = questions; }, [questions]);

  let newAssessmentVar = JSON.parse(
    sessionStorage.getItem("newAssessment") || "false"
  );

  // Use a unique key for each project and set
  const localStorageKey = `responses_${projectid}_${setname}`;
  const draftMetaKey = `draft_meta_${projectid}_${setname}`;

  /**
   * Centralized function to synchronize dropdown scores with latest question values.
   * This ensures consistency by always recalculating scores from the current question.values
   * instead of relying on potentially stale stored scores.
   */
  const syncDropdownScores = useCallback((
    currentResponses: QuestionResponse[],
    currentQuestions: any[]
  ): QuestionResponse[] => {
    if (!currentResponses.length || !currentQuestions.length) {
      return currentResponses;
    }

    console.log("syncDropdownScores: Starting sync with", currentResponses.length, "responses and", currentQuestions.length, "questions");

    return currentResponses.map((res) => {
      const question = currentQuestions.find((q) => q._id === res.questionid);

      // Only update dropdown question scores
      // Normalise response – it may be stored as an array or a plain string
      const normalizedResponse = Array.isArray(res.response) ? res.response[0] : res.response;
      if (question && question.type === "dropdown" && normalizedResponse && normalizedResponse !== "--Select--") {
        const selectedValue = question.values?.find(
          (v: any) => v.response === normalizedResponse
        );

        if (selectedValue && selectedValue.score !== undefined && selectedValue.score !== null) {
          const latestScore = String(selectedValue.score);
          const currentScore = res.score !== undefined && res.score !== null ? String(res.score) : "";

          // Only update if question.values has a valid score AND it differs from stored score
          if (latestScore !== currentScore) {
            console.log(`syncDropdownScores: Updating score for question ${res.questionid}: "${currentScore}" -> "${latestScore}"`);
            return { ...res, score: latestScore };
          }
        }
        // If question.values has no score for this option, preserve the existing stored score
      }

      return res;
    });
  }, []);

  // Load question order from localStorage on mount
  useEffect(() => {
    const savedOrder = localStorage.getItem('question_order_create');
    if (savedOrder) {
      try {
        setQuestionOrder(JSON.parse(savedOrder));
      } catch (error) {
        console.error('Error parsing question order from localStorage:', error);
        setQuestionOrder({});
      }
    }
  }, []);



  // Load draft responses from localStorage when project/set/route changes.
  // Syncs scores immediately if questions are already loaded; otherwise the
  // questions-change effect below will handle it when questions arrive.
  useEffect(() => {
    const savedResponses = JSON.parse(
      localStorage.getItem(localStorageKey) || "[]"
    );
    if (savedResponses.length > 0) {
      const currentNewAssessment = JSON.parse(sessionStorage.getItem("newAssessment") || "false");
      if (currentNewAssessment === true && questionsRef.current.length > 0) {
        // Questions are already loaded – sync scores immediately
        const synced = syncDropdownScores(savedResponses, questionsRef.current);
        setResponses(synced);
        localStorage.setItem(localStorageKey, JSON.stringify(synced));
      } else {
        setResponses(savedResponses);
      }
      setHasSavedDraft(true);
    } else {
      setResponses([]);
      setHasSavedDraft(false);
    }
  }, [projectid, setname, location.pathname, localStorageKey, syncDropdownScores]);

  // Whenever questions load or change (e.g. after Re-Assessment fetch, or returning
  // from CreateQuestion), sync all dropdown scores against the freshest values.
  // Uses functional setResponses so responses never needs to be in the dep array.
  useEffect(() => {
    if (newAssessmentVar !== true || questions.length === 0) return;

    setResponses((currentResponses) => {
      if (!currentResponses.length) return currentResponses;

      console.log("Score sync: questions updated, syncing", currentResponses.length, "responses");
      const synced = syncDropdownScores(currentResponses, questions);
      const hasChanges = synced.some((s, i) => s.score !== currentResponses[i]?.score);

      if (hasChanges) {
        console.log("Score sync: applying updates");
        localStorage.setItem(localStorageKey, JSON.stringify(synced));
        return synced;
      }
      return currentResponses;
    });
  }, [questions, newAssessmentVar, localStorageKey, syncDropdownScores]);

  // Fetch scorewise calculation for all categories
  useEffect(() => {
    const fetchScorewiseData = async () => {
      if (!projectid || !setname || newAssessmentVar === true) return;
      
      try {
        // Use the appropriate API endpoint based on whether it's a project readiness assessment
        const apiEndpoint = fromProjectReadiness
          ? `${process.env.REACT_APP_READINESS_QUESTION_SERVICE_BASE_URL}/score-calculation/${projectid}/${setname}`
          : `${process.env.REACT_APP_QUESTION_SERVICE_BASE_URL}/scorewise-calculation/${projectid}/${setname}`;
        
        console.log(`Fetching scorewise data from: ${apiEndpoint}`);
        
        const res = await axiosInstance.get(apiEndpoint);
        
        if (res.data && res.data.categories && Array.isArray(res.data.categories)) {
          const scorewiseResults: { [key: string]: number } = {};
          
          // Map the categories array to scorewiseData object
          res.data.categories.forEach((categoryData: any) => {
            if (categoryData.category && categoryData.scorewise !== undefined) {
              scorewiseResults[categoryData.category] = parseFloat(categoryData.scorewise);
            }
          });
          
          setScorewiseData(scorewiseResults);
          
          // Pass scores to parent component
          if (onScoreUpdate) {
            onScoreUpdate(scorewiseResults);
          }
        }
      } catch (error) {
        console.error("Error fetching scorewise data", error);
      }
    };
    
    if (newAssessmentVar === false && assessmentData.length > 0) {
      fetchScorewiseData();
    }
  }, [projectid, setname, newAssessmentVar, assessmentData, fromProjectReadiness]);

  const fetchQuestions = async () => {
    try {
      console.log("Fetching questions...");
      console.log("setname before fetch:", setname);

      // Add cache-busting parameter to ensure fresh data
      const timestamp = new Date().getTime();
      
      // Use the appropriate API endpoint based on whether it's a project readiness assessment
      const questionServiceUrl = fromProjectReadiness
        ? process.env.REACT_APP_READINESS_QUESTION_SERVICE_BASE_URL
        : process.env.REACT_APP_QUESTION_SERVICE_BASE_URL;
      
      const questionsEndpoint = fromProjectReadiness
        ? `${questionServiceUrl}/question-readiness?_t=${timestamp}`
        : `${questionServiceUrl}/questions?_t=${timestamp}`;
      
      console.log(`Fetching questions from: ${questionsEndpoint}`);
      
      const res = await axiosInstance.get(
        questionsEndpoint,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      console.log("res", res);

      // Log the response data to check its structure
      console.log("response data", res.data);

      // Ensure setname is defined and log its value
      console.log("setname prop value:", setname);
      console.log("setname type:", typeof setname);
      console.log("setname length:", setname?.length);

      // Temporarily log all items to see if the set field exists
      res.data.forEach((item: any, index: number) => {
        console.log(`Item ${index} - set: "${item.set}", setName: "${item.setName}", isDeleted: ${item.isDeleted}`);
      });

      // Normalize setname for comparison (trim whitespace, handle case)
      const normalizedSetname = setname ? String(setname).trim() : "";
      console.log("Normalized setname for filtering:", normalizedSetname);

      // Filter the data based on setname and exclude only explicitly soft-deleted questions
      // Support both 'set' and 'setName' fields for readiness API compatibility
      const filtered = res.data.filter(
        (item: { set?: any; setName?: any; isDeleted?: boolean }) => {
          const itemSet = item.set ? String(item.set).trim() : "";
          const itemSetName = item.setName ? String(item.setName).trim() : "";
          const matches = (itemSet === normalizedSetname || itemSetName === normalizedSetname);
          const notDeleted = item.isDeleted !== true;
          
          console.log(`Filter check - itemSet: "${itemSet}", itemSetName: "${itemSetName}", matches: ${matches}, notDeleted: ${notDeleted}`);
          
          return matches && notDeleted;
        }
      );
      
      console.log("Filtered questions count:", filtered.length);
      console.log("Filtered questions:", filtered);
      
      // Setting questions triggers the sync effect which will update any loaded responses
      console.log("Questions loaded - setting questions state to trigger score sync");
      setQuestions(filtered);
    } catch (error) {
      console.error("Error fetching the data", error);
      setError("Error fetching the data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (newAssessmentVar === false) {
      fetchAssessmentData();  
    } else {
      fetchQuestions(); 
    }
  }, [newAssessmentVar, location.pathname]); // Added location.pathname to refetch on navigation

  // Refetch questions when page becomes visible (user returns from CreateQuestion)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && newAssessmentVar === true) {
        console.log("Page visible - refetching questions for latest scores");
        fetchQuestions();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [newAssessmentVar]);

  // Note: Score synchronization is handled by the questions-change effect above.
  // questionsRef is used inside loading effects for immediate sync when questions are already available.

  const fetchAssessmentData = async () => {
    try {
      // Use the appropriate API endpoint based on whether it's a project readiness assessment
      const assessmentServiceUrl = fromProjectReadiness
        ? process.env.REACT_APP_READINESS_SERVICE_BASE_URL
        : process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL;
      
      console.log(`Fetching assessment data for: ${projectid}/${setname}`);
      
      let data;
      if (fromProjectReadiness) {
        // Workaround: Use POST createPreprojectAssessment which has working findOne logic
        const response = await axiosInstance.post(
          `${assessmentServiceUrl}/createPreprojectAssessment`,
          {
            preprojectId: projectid,
            setName: setname,
            emailId: [],
            assessmentDetails: [],
            caseStudyCount: 0
          }
        );
        data = response.data;
      } else {
        const response = await axiosInstance.get(
          `${assessmentServiceUrl}/get-projectassesment/${projectid}/${setname}`
        );
        data = response.data;
      }

      // Handle response structure differences between regular and readiness APIs
      let assessmentDataArray: any[];
      if (fromProjectReadiness) {
        // Readiness API returns a single object, wrap it in array for consistency
        if (data && data.assessmentId) {
          assessmentDataArray = [data];
        } else if (data && data.message) {
          // No assessment found yet - treat as new assessment
          assessmentDataArray = [];
        } else {
          assessmentDataArray = Array.isArray(data) ? data : [data];
        }
      } else {
        assessmentDataArray = Array.isArray(data) ? data : [];
      }

      // Normalize property names - readiness uses assessmentDetails, regular uses assessmentdetails
      if (assessmentDataArray.length > 0 && assessmentDataArray[0]) {
        if (assessmentDataArray[0].assessmentDetails && !assessmentDataArray[0].assessmentdetails) {
          assessmentDataArray[0].assessmentdetails = assessmentDataArray[0].assessmentDetails;
        }
      }

      // If no assessment data, check for draft in localStorage and use it
      if (assessmentDataArray.length === 0 || !assessmentDataArray[0].assessmentdetails?.length) {
        dispatch(updateNewAssessmentVariable(true));
        // If a draft exists, load it into responses
        const savedResponses = JSON.parse(
          localStorage.getItem(localStorageKey) || "[]"
        );
        if (savedResponses.length > 0) {
          setResponses(savedResponses);
        } else {
          setResponses([]);
        }
        return;
      }

      const len = assessmentDataArray[0].assessmentdetails.length;
      const latestDetails = assessmentDataArray[0].assessmentdetails[len - 1];

      // Check if qnadetails is empty
      if (!latestDetails.qnadetails || latestDetails.qnadetails.length === 0) {
        setAssessmentData([
          {
            ...assessmentDataArray[0],
            assessmentdetails: [
              {
                ...latestDetails,
                qnadetails: [],
              },
            ],
          },
        ]);
        setLoading(false);
        return;
      }

      // Fetch all questions for this set in ONE API call (batch optimization)
      const questionServiceUrl = fromProjectReadiness
        ? process.env.REACT_APP_READINESS_QUESTION_SERVICE_BASE_URL
        : process.env.REACT_APP_QUESTION_SERVICE_BASE_URL;
      
      const questionsBySetEndpoint = fromProjectReadiness
        ? `${questionServiceUrl}/questionsReadiness-by-set/${setname}`
        : `${questionServiceUrl}/questions-by-set/${setname}`;
      
      console.log(`Fetching questions by set from: ${questionsBySetEndpoint}`);
      
      const questionsResponse = await axiosInstance.get(questionsBySetEndpoint);
      
      // Create a lookup map: questionId -> question data
      const questionsMap: Map<string, any> = new Map(
        questionsResponse.data.map((q: any) => [q._id, q])
      );

      // Map qnadetails with question data from the lookup
      const qnadetailsWithQuestions: QnaDetail[] = latestDetails.qnadetails
        .map((item: any): QnaDetail | null => {
          const question: any = questionsMap.get(item.questionid);
          
          if (!question) {
            console.warn(`Question not found for ID: ${item.questionid}`);
            return null;
          }

          // Use the stored score as-is in view mode.
          // Score recalculation from latest question.values only happens in Re-Assessment mode (syncDropdownScores).
          const calculatedScore = item.score !== undefined && item.score !== null ? String(item.score) : "";

          return {
            questionid: item.questionid,
            category: question.category || "",
            set_name: question.set || question.setName || setname,
            description: question.description || "",
            type: question.type || "text",
            values: question.values || [], // Include values array with latest scores for dropdowns
            response: (item.response as Response) || "",
            remarks: item.remarks || "",
            isRequire: question.isRequire ?? false, // Default to false if undefined
            score: calculatedScore,
          };
        })
        .filter((entry: QnaDetail | null): entry is QnaDetail => entry !== null); // Type guard to satisfy TS

      setAssessmentData([
        {
          ...assessmentDataArray[0],
          assessmentdetails: [
            {
              ...latestDetails,
              qnadetails: qnadetailsWithQuestions,
            },
          ],
        },
      ]);
      // If a draft exists, prefer showing draft in the UI (responses), but keep assessmentData for summary
      const savedResponses = JSON.parse(
        localStorage.getItem(localStorageKey) || "[]"
      );
      if (savedResponses.length > 0) {
        setResponses(savedResponses);
      } else {
        // If no draft, show latest assessment responses
        // Ensure scores are properly converted to strings for consistency
        const res = qnadetailsWithQuestions.map((qna: any) => {
          const { description, ...rest } = qna;
          return {
            ...rest,
            score: rest.score !== undefined && rest.score !== null ? String(rest.score) : ""
          };
        });
        setResponses(res);
      }
    } catch (error) {
      console.error("Error fetching assessment data", error);
      setError("Error fetching assessment data");
    } finally {
      setLoading(false);
    }
  };

  // Load responses for reassessment mode.
  // Runs whenever newAssessmentVar switches to true OR assessmentData arrives.
  // Syncs scores immediately using questionsRef if questions are already loaded,
  // otherwise the questions-change effect handles sync when fetchQuestions() completes.
  useEffect(() => {
    if (
      newAssessmentVar === true &&
      assessmentData.length > 0 &&
      assessmentData[0]?.assessmentdetails?.[0]?.qnadetails
    ) {
      console.log("Reassessment: loading responses");
      const savedResponses = JSON.parse(
        localStorage.getItem(localStorageKey) || "[]"
      );

      let loadedResponses: QuestionResponse[];
      if (savedResponses.length > 0) {
        console.log("Reassessment: using saved draft");
        loadedResponses = savedResponses;
      } else {
        console.log("Reassessment: using last submitted data");
        const qnadetails = assessmentData[0].assessmentdetails[0].qnadetails;
        loadedResponses = qnadetails.map((qna: any) => {
          const { description, ...rest } = qna;
          return {
            ...rest,
            score: rest.score !== undefined && rest.score !== null ? String(rest.score) : ""
          };
        });
      }

      // If questions are already in memory, sync scores immediately using latest values.
      // Otherwise, the questions-change effect will sync once fetchQuestions() completes.
      if (questionsRef.current.length > 0) {
        console.log("Reassessment: questions ready – syncing scores immediately");
        const synced = syncDropdownScores(loadedResponses, questionsRef.current);
        setResponses(synced);
        localStorage.setItem(localStorageKey, JSON.stringify(synced));
      } else {
        console.log("Reassessment: questions not ready yet – will sync after fetch");
        setResponses(loadedResponses);
      }
    }
  }, [assessmentData, newAssessmentVar, localStorageKey, syncDropdownScores]);

  const handleDiscardDraft = () => {
    setShowDiscardDialog(true);
  };

  const confirmDiscardDraft = () => {
    // Remove draft from localStorage
    localStorage.removeItem(localStorageKey);
    // Remove draft meta data
    localStorage.removeItem(draftMetaKey);
    
    // Reset responses to empty or load from last submitted assessment
    if (assessmentData.length > 0 && assessmentData[0]?.assessmentdetails?.[0]?.qnadetails) {
      const qnadetails = assessmentData[0].assessmentdetails[0].qnadetails;
      
      // Load responses from assessment data
      const res = qnadetails.map((qna: any) => {
        const { description, ...rest } = qna;
        return {
          ...rest,
          score: rest.score !== undefined && rest.score !== null ? String(rest.score) : ""
        };
      });
      
      // Sync dropdown scores with latest question values using centralized function
      const syncedResponses = syncDropdownScores(res, questions);
      setResponses(syncedResponses);
    } else {
      setResponses([]);
    }
    
    setHasSavedDraft(false);
    setShowDiscardDialog(false);
    toast.success("Draft discarded successfully");
    onData([], Object.keys(groupedByLatestCategory).length, false);
  };

  const cancelDiscardDraft = () => {
    setShowDiscardDialog(false);
  };

  const sendResponses = () => {
    // Always save the current responses as draft
    localStorage.setItem(localStorageKey, JSON.stringify(responses));
    // Save project status and reason for change
    const draftMeta = {
      projectstatus: projectstatus || "",
      reasonforchange: reasonforchange || ""
    };
    localStorage.setItem(draftMetaKey, JSON.stringify(draftMeta));
    setHasSavedDraft(true);
    // Find all required questions
    const requiredQuestions = questions.filter((q) => q.isRequire);
    // Find missing responses for required questions
    const missingResp = requiredQuestions
      .filter((q) => {
        const res = responses.find((r) => r.questionid === q._id);
        // For dropdown, text, radio, checkbox
        if (!res) return true;
        if (q.type === "checkbox") {
          return !Array.isArray(res.response) || res.response.length === 0;
        }
        return (
          !res.response || res.response === "" || res.response === "--Select--"
        );
      })
      .map((q) => q._id);

    // Find missing remarks for required questions
    const missingRem = requiredQuestions
      .filter((q) => {
        const res = responses.find((r) => r.questionid === q._id);
        return !res || !res.remarks || res.remarks.trim() === "";
      })
      .map((q) => q._id);

    setMissingResponses(missingResp);
    setMissingRemarks(missingRem);

    // If any required fields are missing, do not show success toast
    if (missingResp.length > 0 || missingRem.length > 0) {
      toast.error("Please answer all required questions before saving.");
      onData(responses, Object.keys(groupedByLatestCategory).length, false); // invalid draft
    } else {
      toast.success("Assessment saved successfully");
      onData(responses, Object.keys(groupedByLatestCategory).length, true); // valid draft
    }

    // Do not remove the draft here; only remove after final submission in parent
    // Always reflect the latest responses in the UI (already handled by setResponses)
  };
  // const sendResponses = () => {
  //   const unansweredMandatory = questions.filter((q) => {
  //     if (!q.isRequire) return false;

  //     const res = responses.find((r) => r.questionid === q._id || r.questionid === q.questionid);
  //     if (!res) return true;

  //     if (q.type === "checkbox") {
  //       return !Array.isArray(res.response) || res.response.length === 0;
  //     }

  //     return res.response === "" || res.response === "--Select--";
  //   });

  //   if (unansweredMandatory.length > 0) {
  //     toast.error("Please answer all mandatory questions before submitting.");
  //     return;
  //   }

  //   onData(responses, Object.keys(groupedByLatestCategory).length, true);
  //   localStorage.setItem(`responses_${projectid}`, JSON.stringify(responses));
  // };


  const updateResponse = (
    questionid: string,
    response: Response,
    category: string,
    isRequire: boolean,
    score?: string | number
  ) => {
    setResponses((prevResponses) => {
      const existingResponseIndex = prevResponses.findIndex(
        (q) => q.questionid === questionid
      );
      const updatedResponses = [...prevResponses];
      
      // Normalize score to string for consistent storage
      const normalizedScore = score !== undefined && score !== null ? String(score) : undefined;
      
      if (existingResponseIndex !== -1) {
        updatedResponses[existingResponseIndex] = {
          ...updatedResponses[existingResponseIndex],
          response,
          score: normalizedScore,
        };
      } else {
        updatedResponses.push({
          category,
          questionid,
          response,
          remarks: "",
          isRequire,
          score: normalizedScore,
        });
      }
      // Save draft to localStorage
      localStorage.setItem(localStorageKey, JSON.stringify(updatedResponses));
      return updatedResponses;
    });
  };

 
  const updateRemarks = (
    questionid: string,
    remarks: string,
    category: string,
    isRequire: boolean,
    score?: string | number
  ) => {
    setResponses((prevResponses) => {
      const existingResponseIndex = prevResponses.findIndex(
        (q) => q.questionid === questionid
      );
      const updatedResponses = [...prevResponses];
      
      // Normalize score to string for consistent storage
      const normalizedScore = score !== undefined && score !== null ? String(score) : undefined;
      
      if (existingResponseIndex !== -1) {
        updatedResponses[existingResponseIndex] = {
          ...updatedResponses[existingResponseIndex],
          remarks,
          score: normalizedScore,
        };
      } else {
        updatedResponses.push({
          category,
          questionid,
          response: "",
          remarks,
          isRequire,
          score: normalizedScore,
        });
      }
      // Save draft to localStorage
      localStorage.setItem(localStorageKey, JSON.stringify(updatedResponses));
      return updatedResponses;
    });
  };

  // Helper: Get response by question ID
  const getResponseByQuestionId = (id: string): Response => {
    const res = responses.find((res) => res.questionid === id);
    if (res) {
      return res.response;
    }
    return "";
  };

  // Helper: Get score by question ID from responses state
  const getScoreByQuestionId = (id: string): string => {
    const res = responses.find((res) => res.questionid === id);
    // Return score even if it's "0" or empty string - these are valid states
    return res?.score !== undefined && res?.score !== null ? String(res.score) : "";
  };

  // Helper: Get score for a dropdown selection from question values
  const getDropdownScoreByResponse = (question: any, selectedResponse: string): string => {
    if (question.type !== "dropdown" || !selectedResponse || !question.values) {
      return "";
    }
    const found = question.values.find((v: any) => v.response === selectedResponse);
    return found?.score !== undefined && found?.score !== null ? String(found.score) : "";
  };

  const handleOpenClose = (set: string) => {
    setIsOpen((prevIsOpen) => {
      const newIsOpen: { [key: string]: boolean } = {};
      Object.keys(prevIsOpen).forEach((key) => {
        newIsOpen[key] = key === set ? !prevIsOpen[key] : false;
      });
      newIsOpen[set] = !prevIsOpen[set]; // Ensure the clicked category toggles
      return newIsOpen;
    });
  };

  const groupQuestionsByCategory = (questions: any) => {
    return questions.reduce((acc: any, question: any) => {
      const category = question.category;
      if (!acc[category]) acc[category] = [];
      acc[category].push(question);
      return acc;
    }, {});
  };

  // Get sorted questions for a category based on questionOrder state
  const getSortedQuestions = (setName: string, categoryName: string, questions: any[]) => {
    const key = `${setName}||${categoryName}`;
    if (!questionOrder[key] || questionOrder[key].length === 0) {
      return questions;
    }

    const orderMap = new Map(questionOrder[key].map((id, index) => [id, index]));
    
    return [...questions].sort((a, b) => {
      const idA = a._id?.toString() || a.questionid?.toString() || '';
      const idB = b._id?.toString() || b.questionid?.toString() || '';
      const indexA = orderMap.get(idA) ?? Infinity;
      const indexB = orderMap.get(idB) ?? Infinity;
      return indexA - indexB;
    });
  };

  if (loading) {
    return <Loading />;
  }
  let setValue: string;
  setValue = setname;

  let groupedByLatestCategory: any;

  if (newAssessmentVar === true) {
    groupedByLatestCategory = groupQuestionsByCategory(questions);
  }
  if (newAssessmentVar === false) {
    groupedByLatestCategory = groupQuestionsByCategory(
      assessmentData[0]?.assessmentdetails?.[0]?.qnadetails || []
    );
  }

  return (
    <div className="questions" style={{ backgroundColor: "transparent" }}>
      {newAssessmentVar === true &&
        Object.keys(groupedByLatestCategory).map((category, index) => (
          <div key={index}>
            <div
              className="questionBtn"
              onClick={() => handleOpenClose(category)}
              style={{ display: "flex", alignItems: "center" }}
            >
              {isOpen[category] ? (
                <IoIosArrowDown style={{ marginRight: "8px" }} />
              ) : (
                <IoIosArrowForward style={{ marginRight: "8px" }} />
              )}
              <p style={{ color: "black" }}>{category}</p>
            </div>

            {isOpen[category] && (
              <div>
                <table className="questionTable" style={{}}>
                  <thead>
                    <tr>
                      <td style={{ width: "3%", padding: "8px 4px", verticalAlign: "middle" }}>#</td>
                      <td style={{ width: "47%", padding: "8px", verticalAlign: "middle" }}>Questions</td>
                      <td style={{ width: "20%", padding: "8px", verticalAlign: "middle" }} className="responseIsComplusory">Response</td>
                      <td style={{ width: "20%", padding: "8px", verticalAlign: "middle" }}>Remarks</td>
                      <td style={{ width: "10%", textAlign: "center", padding: "8px", verticalAlign: "middle" }}>Score</td>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedQuestions(setValue, category, groupedByLatestCategory[category]).map(
                      (question: any, idx: any) => {
                            // Find selected response for this question
                            const selectedResponse = getResponseByQuestionId(
                              question._id
                            );
                            // Find score for selected response (for dropdown)
                            let selectedScore = "";
                            if (question.type === "dropdown" && selectedResponse) {
                              const found = question.values.find(
                                (v: any) => v.response === selectedResponse
                              );
                              selectedScore = found ? found.score : "";
                            }
                            return (
                              <tr key={question._id}>
                                <td style={{ width: "3%", textAlign: "center", verticalAlign: "middle", padding: "8px 4px" }}>
                                  {idx + 1}
                                </td>
                                <td style={{ width: "47%", textAlign: "left", verticalAlign: "middle", padding: "8px" }}>
                              {question.description}
                            </td>
                            <td style={{ width: "20%", textAlign: "left", verticalAlign: "middle", padding: "8px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                {/* Add * for mandatory */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {question.isRequire && (
                                    <span
                                      style={{ color: "red", marginRight: 4 }}
                                    >
                                      *
                                    </span>
                                  )}
                                  {question.type === "dropdown" ? (
                                    <select
                                      name="dropdown"
                                      value={
                                        (getResponseByQuestionId(
                                          question._id
                                        ) as string) || "--Select--"
                                      }
                                      onChange={(e) => {
                                        // Find the selected value's score from latest question.values
                                        const selectedValue = question.values?.find(
                                          (v: any) => v.response === e.target.value
                                        );
                                        // Convert score to string for consistency, use latest value from question
                                        const selectedScore = selectedValue?.score !== undefined && selectedValue?.score !== null
                                          ? String(selectedValue.score)
                                          : "";
                                        
                                        // Update response and score immediately
                                        updateResponse(
                                          question._id,
                                          e.target.value,
                                          question.category,
                                          question.isRequire,
                                          selectedScore
                                        );
                                      }}
                                      style={
                                        question.isRequire &&
                                        missingResponses.includes(question._id)
                                          ? { border: "1px solid red" }
                                          : {}
                                      }
                                    >
                                      <option value="--Select--">
                                        --Select--
                                      </option>
                                      {question.values.map((item: any) => (
                                        <option
                                          value={item.response}
                                          key={item.option}
                                        >
                                          {item.response}
                                        </option>
                                      ))}
                                    </select>
                                  ) : question.type === "text" ? (
                                    <input
                                      type="text"
                                      name="text"
                                      placeholder="Enter the Answer"
                                      value={
                                        (getResponseByQuestionId(
                                          question._id
                                        ) as string) || ""
                                      }
                                      onChange={(e) =>
                                        updateResponse(
                                          question._id,
                                          e.target.value,
                                          question.category,
                                          question.isRequire
                                        )
                                      }
                                      style={
                                        question.isRequire &&
                                        missingResponses.includes(question._id)
                                          ? { border: "1px solid red" }
                                          : {}
                                      }
                                    />
                                  ) : question.type === "radio" ? (
                                    <div className="radio">
                                      {question.values.map((item: any) => (
                                        <div
                                          key={item.option}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <input
                                            type="radio"
                                            id={`${item.response}_${question._id}`}
                                            name={`radio_${question._id}`}
                                            value={item.response}
                                            checked={
                                              getResponseByQuestionId(
                                                question._id
                                              ) === item.response
                                            }
                                            onChange={(e) =>
                                              updateResponse(
                                                question._id,
                                                e.target.value,
                                                question.category,
                                                question.isRequire
                                              )
                                            }
                                            style={
                                              question.isRequire &&
                                              missingResponses.includes(
                                                question._id
                                              )
                                                ? { outline: "1px solid red" }
                                                : {}
                                            }
                                          />
                                          <label
                                            htmlFor={`${item.response}_${question._id}`}
                                          >
                                            {item.response}
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    question.type === "checkbox" && (
                                      <div className="checkbox">
                                        {question.values.map((item: any) => (
                                          <div
                                            key={item.option}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              name={item.response}
                                              value={item.response}
                                              checked={
                                                Array.isArray(
                                                  getResponseByQuestionId(
                                                    question._id
                                                  ) as string[]
                                                ) &&
                                                (
                                                  getResponseByQuestionId(
                                                    question._id
                                                  ) as string[]
                                                ).includes(item.response)
                                              }
                                              onChange={(e) => {
                                                const currentResponses =
                                                  (getResponseByQuestionId(
                                                    question._id
                                                  ) as string[]) || [];
                                                const newResponse =
                                                  currentResponses.includes(
                                                    item.response
                                                  )
                                                    ? currentResponses.filter(
                                                        (r) =>
                                                          r !== item.response
                                                      )
                                                    : [
                                                        ...currentResponses,
                                                        item.response,
                                                      ];
                                                updateResponse(
                                                  question._id,
                                                  newResponse,
                                                  question.category,
                                                  question.isRequire
                                                );
                                              }}
                                              style={
                                                question.isRequire &&
                                                missingResponses.includes(
                                                  question._id
                                                )
                                                  ? { outline: "1px solid red" }
                                                  : {}
                                              }
                                            />
                                            <label htmlFor={item.response}>
                                              {item.response}
                                            </label>
                                          </div>
                                        ))}
                                      </div>
                                    )
                                  )}
                                </div>
                                {/* Error message for response */}
                                {question.isRequire &&
                                  missingResponses.includes(question._id) && (
                                    <span
                                      style={{ color: "red", fontSize: "12px",translate:'8px 7px' }}
                                    >
                                      You have to fill this field
                                    </span>
                                  )}
                              </div>
                            </td>
                            <td style={{ width: "20%", textAlign: "left", verticalAlign: "middle", padding: "8px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {question.isRequire && (
                                    <span
                                      style={{ color: "red", marginRight: 4 }}
                                    >
                                      *
                                    </span>
                                  )}
                                  <input
                                    type="text"
                                    name="remarks"
                                    value={
                                      responses.find(
                                        (res) => res.questionid === question._id
                                      )?.remarks || ""
                                    }
                                    onChange={(e) => {
                                      // Preserve existing score when updating remarks
                                      const existingScore = getScoreByQuestionId(question._id);
                                      updateRemarks(
                                        question._id,
                                        e.target.value,
                                        question.category,
                                        question.isRequire,
                                        existingScore || undefined
                                      );
                                    }}
                                    placeholder={
                                      question.isRequire
                                        ? "Enter your remarks here (required)"
                                        : "Enter your remarks here (optional)"
                                    }
                                    style={
                                      question.isRequire &&
                                      missingRemarks.includes(question._id)
                                        ? { border: "1px solid red" }
                                        : {}
                                    }
                                  />
                                </div>
                                {question.isRequire &&
                                  missingRemarks.includes(question._id) && (
                                    <span
                                      style={{ color: "red", fontSize: "12px",translate:'8px 7px' }}
                                    >
                                      You have to fill this field
                                    </span>
                                  )}
                              </div>
                            </td>
                            <td style={{ width: "10%", textAlign: "center", verticalAlign: "middle", padding: "8px" }}>
                              {/* Display score - dynamically calculated for dropdowns to reflect latest values */}
                              {(() => {
                                // For dropdown questions, calculate score from current selection
                                if (question.type === "dropdown") {
                                  const selectedResponse = getResponseByQuestionId(question._id);
                                  if (selectedResponse && selectedResponse !== "--Select--") {
                                    // First: try to get score from latest question.values (most up-to-date)
                                    const selectedValue = question.values?.find(
                                      (v: any) => v.response === selectedResponse
                                    );
                                    if (selectedValue && selectedValue.score !== undefined && selectedValue.score !== null) {
                                      return String(selectedValue.score);
                                    }
                                    // Fallback: use stored score from responses state
                                    // (covers cases where question.values has no score defined for this option)
                                    const storedScore = getScoreByQuestionId(question._id);
                                    if (storedScore !== "") {
                                      return storedScore;
                                    }
                                  }
                                  return "";
                                }
                                
                                // For non-dropdown questions (text, radio, checkbox), display hyphen instead of 0
                                return "-";
                              })()}
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}

      {newAssessmentVar === false &&
        Object.keys(groupedByLatestCategory).length > 0 &&
        Object.keys(groupedByLatestCategory).map((set, index) => (
          <div key={set}>
            <div
              className="questionBtn"
              onClick={() => handleOpenClose(set)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "96%",minHeight: "32px", padding: "0 24px", }}
            >
              <div style={{ display: "flex", alignItems: "center", minWidth: "300px" }}>
              {isOpen[set] ? (
                <IoIosArrowDown style={{ marginRight: "8px" }} />
              ) : (
                <IoIosArrowForward style={{ marginRight: "8px" }} />
              )}
              <p style={{ color: "black",margin:0 }}>{set}</p>
               </div>
               {/* Display scorewise calculation result */}
               {scorewiseData[set] !== undefined && (() => {
                 const score = scorewiseData[set] * 100;
                 const size = 50;
                 const getColor = (score: number): string => {
                   if (score >= 85) return '#22c55e'; // Green
                   if (score >= 65) return '#f59e0b'; // Amber
                   return '#ef4444'; // Red
                 };
                 const color = getColor(score);
                 const radius = (size - 8) / 2;
                 const circumference = 2 * Math.PI * radius;
                 const offset = circumference - (score / 100) * circumference;
                 
                 return (
                   <div style={{ position: 'relative', width: size, height: size, display: 'inline-block' }}>
                     <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                       {/* Background circle */}
                       <circle
                         cx={size / 2}
                         cy={size / 2}
                         r={radius}
                         fill="none"
                         stroke="#e5e7eb"
                         strokeWidth="4"
                       />
                       {/* Progress circle */}
                       <circle
                         cx={size / 2}
                         cy={size / 2}
                         r={radius}
                         fill="none"
                         stroke={color}
                         strokeWidth="4"
                         strokeDasharray={circumference}
                         strokeDashoffset={offset}
                         strokeLinecap="round"
                         style={{
                           transition: 'stroke-dashoffset 0.5s ease',
                         }}
                       />
                     </svg>
                     {/* Score text in the center */}
                     <div
                       style={{
                         position: 'absolute',
                         top: '50%',
                         left: '50%',
                         transform: 'translate(-50%, -50%)',
                         fontSize: size / 4.5,
                         fontWeight: '700',
                         color: color,
                         fontFamily: 'Arial, sans-serif',
                         letterSpacing: '-0.5px',
                         lineHeight: 1,
                       }}
                     >
                       {score.toFixed(1)}%
                     </div>
                   </div>
                 );
               })()}
            </div>
            {isOpen[set] && (
              <div>
                <table className="questionTable" style={{}}>
                  <thead>
                    <tr>
                      <td style={{ width: "3%", borderBottomLeftRadius: "3px", padding: "8px 4px", verticalAlign: "middle" }}>#</td>
                      <td style={{ width: "47%", padding: "8px", verticalAlign: "middle" }}>Questions</td>
                      <td style={{ width: "20%", padding: "8px", verticalAlign: "middle" }} className="responseIsComplusory">Response</td>
                      <td style={{ width: "20%", padding: "8px", verticalAlign: "middle" }}>Remarks</td>
                      <td style={{ width: "10%", textAlign: "center", borderBottomRightRadius: "3px", padding: "8px", verticalAlign: "middle" }}>Score</td>
                    </tr>
                  </thead>
                  <tbody>
                    {newAssessmentVar === false &&
                      getSortedQuestions(setValue, set, groupedByLatestCategory[set]).map(
                        (question: any, index: any) => (
                          <tr
                            key={question.questionid}
                            className="summaryRow"
                          >
                            <td style={{ width: "3%", textAlign: "center", verticalAlign: "middle", padding: "8px 4px" }}>
                              {index + 1}
                            </td>
                            <td style={{ width: "47%", textAlign: "left", verticalAlign: "middle", padding: "8px" }}>
                              {question.description}
                            </td>
                            <td
                              style={{ width: "20%", textAlign: "left", verticalAlign: "middle", padding: "8px" }}
                            >
                              {question.response[0]}
                            </td>
                            <td
                              style={{ width: "20%", textAlign: "left", verticalAlign: "middle", padding: "8px" }}
                            >
                              {question.remarks}
                            </td>
                            <td style={{ width: "10%", textAlign: "center", verticalAlign: "middle", padding: "8px" }}>
                              {question.type === "dropdown" ? question.score : "-"}
                            </td>
                          </tr>
                        )
                      )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}

      {/* Display message when qnadetails is empty */}
      {newAssessmentVar === false &&
        assessmentData.length > 0 &&
        assessmentData[0]?.assessmentdetails?.[0]?.qnadetails?.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              margin: "20px 0",
            }}
          >
            <p
              style={{
                fontSize: "18px",
                color: "#666",
                margin: "0 0 10px 0",
              }}
            >
              No assessment has been added yet.
            </p>
            <p
              style={{
                fontSize: "16px",
                color: "#999",
                margin: 0,
              }}
            >
              Please perform a Re-Assessment to add assessment data.
            </p>
          </div>
        )}

      {newAssessmentVar === true && (
        <div
          className="saveBtnContainer"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          {hasSavedDraft && (
            <div
              className="savebtn"
              style={{ 
                backgroundColor: "#0d5eff",
                cursor: "pointer",
              }}
              onClick={handleDiscardDraft}
            >
              Discard Draft
            </div>
          )}
          <div
            className="savebtn"
            style={{ backgroundColor: "#0d5eff" }}
            onClick={sendResponses}
          >
            Save As Draft
          </div>
        </div>
      )}

      {/* Discard Draft Confirmation Dialog */}
      <Dialog
        open={showDiscardDialog}
        onClose={cancelDiscardDraft}
        PaperProps={{
          style: {
            borderRadius: 12,
            padding: 0,
            boxShadow: "0 12px 40px rgba(15,23,42,0.12)",
            overflow: "hidden",
            background: "#ffffff",
          },
        }}
      >
        <DialogTitle
          style={{
            fontWeight: 700,
            fontSize: "1.25rem",
            padding: "20px 24px 8px",
            color: "#072A49",
            borderBottom: "1px solid #eef2f6",
          }}
        >
          Discard Draft?
        </DialogTitle>
        <DialogContent style={{ padding: "18px 24px 6px 24px" }}>
          <DialogContentText style={{ color: "#4b5563", fontSize: 14, lineHeight: 1.5 }}>
            Are you sure you want to discard the saved draft? This action cannot be undone and all your draft progress will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ justifyContent: "flex-end", padding: "14px 20px 20px 20px" }}>
          <Button
            onClick={cancelDiscardDraft}
            style={{
              background: "linear-gradient(90deg,#0b74ff,#0066cc)",
              color: "#fff",
              boxShadow: "0 8px 28px rgba(107,114,128,0.14)",
              borderRadius: 10,
              padding: "10px 14px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              minWidth: 100,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDiscardDraft}
            style={{
              background: "linear-gradient(90deg,#0b74ff,#0066cc)",
              color: "#fff",
              boxShadow: "0 8px 28px rgba(11,116,255,0.14)",
              borderRadius: 10,
              padding: "10px 14px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              minWidth: 100,
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Question;